import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Sun, ArrowDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Studies from '../components/Studies';
import Projects from '../components/Projects';
import Gallery from '../components/Gallery';
import { fetchPublicSettings, pickCmsShort } from '../services/publicContentService';

const ServicesPage = () => {
  const { theme } = useOutletContext();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [cms, setCms] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('services_hero').then((v) => { if (alive) setCms(v || {}); });
    return () => { alive = false; };
  }, []);

  const eyebrow   = pickCmsShort(cms.eyebrow,        isAr) || (isAr ? 'الخدمات والمشاريع' : 'Services & Projects');
  const line1     = pickCmsShort(cms.title_line1,    isAr) || (isAr ? 'خدماتنا' : 'Our');
  const line2     = pickCmsShort(cms.title_line2,    isAr) || (isAr ? 'ومشاريعنا' : 'Services & Projects');
  const subtitle  = pickCmsShort(cms.subtitle,       isAr) || (isAr
    ? 'حلول استشارية متكاملة في مختلف المجالات البيئية، مدعومة بسجل من المشاريع النوعية الممتدة عبر المملكة.'
    : 'Integrated environmental consultancy spanning every discipline — backed by a track record of projects across the Kingdom.');
  const bgHero    = (cms.background && String(cms.background).trim())
    || 'https://images.unsplash.com/photo-1547234935-80c7145ec969?w=2400&q=85';
  const bgSection = (cms.section_background && String(cms.section_background).trim())
    || 'https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?w=2400&q=80';

  return (
    <div className="services-desert-theme" dir={isAr ? 'rtl' : 'ltr'}>
      {/* IMMERSIVE DESERT HERO */}
      <section className="relative h-[100vh] min-h-[680px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${bgHero}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-orange-900/30 via-amber-900/40 to-orange-950/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-orange-950/30" />

        <div
          className="absolute top-[12%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,196,87,0.45) 0%, rgba(245,135,32,0.25) 30%, rgba(245,135,32,0) 70%)',
            filter: 'blur(30px)',
          }}
        />

        <div className="absolute inset-0 pointer-events-none">
          {[...Array(45)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${1 + Math.random() * 2.5}px`,
                height: `${1 + Math.random() * 2.5}px`,
                background: `rgba(${245 + Math.random() * 10},${180 + Math.random() * 50},${100 + Math.random() * 60},${0.4 + Math.random() * 0.4})`,
                animation: `sand-drift ${10 + Math.random() * 15}s linear infinite`,
                animationDelay: `${Math.random() * 10}s`,
              }}
            />
          ))}
        </div>

        <svg className="absolute bottom-0 left-0 w-full pointer-events-none" height="280" viewBox="0 0 1440 280" preserveAspectRatio="none">
          <defs>
            <linearGradient id="dune1" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#7a4a1a" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#3d2510" stopOpacity="0.85" />
            </linearGradient>
            <linearGradient id="dune2" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#5d3812" stopOpacity="0.75" />
              <stop offset="100%" stopColor="#1f1206" stopOpacity="0.95" />
            </linearGradient>
          </defs>
          <path d="M0,160 C240,90 480,180 720,130 C960,80 1200,180 1440,120 L1440,280 L0,280 Z" fill="url(#dune1)" />
          <path d="M0,220 C200,170 460,240 720,210 C980,180 1240,250 1440,210 L1440,280 L0,280 Z" fill="url(#dune2)" />
        </svg>

        <div className="relative h-full flex items-center justify-center container mx-auto px-4">
          <div className="text-center max-w-4xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-orange-200/40 mb-8 backdrop-blur-md">
              <Sun className="w-4 h-4 text-orange-200" />
              <span className="text-xs tracking-[0.4em] uppercase text-orange-100 font-semibold" data-testid="services-hero-eyebrow">
                {eyebrow}
              </span>
            </div>
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8 text-white leading-[1.05]"
              style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6), 0 0 60px rgba(245,135,32,0.3)' }}
              data-testid="services-hero-title"
            >
              {line1}
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-200 via-orange-300 to-orange-500">
                {line2}
              </span>
            </h1>
            <p
              className="text-lg md:text-xl text-orange-50/95 max-w-2xl mx-auto leading-loose mb-12 font-light"
              style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
              data-testid="services-hero-subtitle"
            >
              {subtitle}
            </p>
            <a href="#studies" className="group inline-flex flex-col items-center gap-2 text-orange-100 hover:text-white transition-colors">
              <span className="text-sm tracking-[0.3em] uppercase font-semibold">{isAr ? 'استكشف' : 'Explore'}</span>
              <ArrowDown className="w-5 h-5 group-hover:translate-y-1 transition-transform animate-bounce" />
            </a>
          </div>
        </div>
      </section>

      {/* Continuous desert atmosphere wraps the content sections */}
      <div className="relative">
        {/* Persistent desert background (fixed so it stays as user scrolls) */}
        <div
          className="fixed inset-0 -z-10 bg-cover bg-center"
          style={{
            backgroundImage: `url('${bgSection}')`,
          }}
        />
        <div className="fixed inset-0 -z-10 bg-gradient-to-b from-amber-50/95 via-orange-50/90 to-amber-100/95 dark:from-amber-900/55 dark:via-orange-800/45 dark:to-amber-900/55" />

        {/* Subtle floating sand particles across the entire page */}
        <div className="fixed inset-0 -z-10 pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${1 + Math.random() * 2}px`,
                height: `${1 + Math.random() * 2}px`,
                background: `rgba(245,180,100,${0.25 + Math.random() * 0.3})`,
                animation: `sand-drift ${15 + Math.random() * 15}s linear infinite`,
                animationDelay: `${Math.random() * 15}s`,
              }}
            />
          ))}
        </div>

        {/* Soft warm halos */}
        <div className="fixed top-[20%] left-[5%] w-[500px] h-[500px] rounded-full blur-[120px] bg-orange-300/25 pointer-events-none -z-10" />
        <div className="fixed bottom-[20%] right-[5%] w-[500px] h-[500px] rounded-full blur-[120px] bg-amber-300/25 pointer-events-none -z-10" />

        <Studies />
        <Projects theme={theme} />
        <Gallery />
      </div>
    </div>
  );
};

export default ServicesPage;
